import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import type { CSSProperties } from 'react';
import { ArrowLeft, ArrowRight, PencilLine } from 'lucide-react';
import { findGrammarTopic, type GrammarBlock } from '@org/grammar-content';
import { GRAMMAR_EXERCISES } from '@org/grammar-content';
import { GrammarPractice } from '../../../components/grammar-practice';
import { GrammarLevelPill } from '../../../components/grammar-level-pill';

// -- Feminist color palette (local copy — same convention as journey.tsx / mural.tsx) --
const P = {
  maxRed: '#D41920',
  charm: '#F2A0C4',
  baby: '#F8D7DD',
  white: '#FFFFFF',
  cream: '#FFF8F5',
  blush: '#FDF0ED',
  roseMist: '#F9E4E0',
  warmGray: '#756560',
  darkText: '#2D1A1F',
  midText: '#6B4F55',
  lightText: '#8A6F69',
  lightBorder: '#F0DAD5',
  shadow: 'rgba(180, 80, 80, 0.12)',
};

const oswald = "'Oswald', sans-serif";
const dmSerif = "'DM Serif Text', serif";

const TOPIC_STYLES = `
.ga-nav-link:hover { color: ${P.maxRed}; }
.ga-nav-link:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.ga-table-wrap { overflow-x: auto; }
`;

const bodyStyle: CSSProperties = {
  color: P.midText,
  fontSize: 15.5,
  lineHeight: 1.65,
  margin: 0,
};

export const Route = createFileRoute('/_app/grammar-aid/$topicSlug')({
  component: GrammarTopicPage,
});

// Content strings may mark the grammar target with **double asterisks**
// (e.g. "Maria **went** to the beach."); render those spans highlighted,
// the way the original guide bolded them.
function Inline({ text }: { text: string }) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <strong key={index} style={{ color: P.maxRed, fontWeight: 700, fontStyle: 'normal' }}>
            {part}
          </strong>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </>
  );
}

function BlockView({ block }: { block: GrammarBlock }) {
  switch (block.kind) {
    case 'p':
      return (
        <p style={bodyStyle}>
          <Inline text={block.text} />
        </p>
      );
    case 'h3':
      return (
        <h3
          style={{
            fontFamily: oswald,
            fontWeight: 600,
            fontSize: 14,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: P.darkText,
            margin: '10px 0 0',
          }}
        >
          {block.text}
        </h3>
      );
    case 'examples':
      return (
        <div
          style={{
            background: P.blush,
            borderLeft: `3px solid ${P.maxRed}`,
            borderRadius: 6,
            padding: '12px 14px',
          }}
        >
          <p
            style={{
              fontFamily: oswald,
              fontWeight: 600,
              fontSize: 11,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: P.maxRed,
              margin: '0 0 6px',
            }}
          >
            e.g.
          </p>
          <div style={{ display: 'grid', gap: 4 }}>
            {block.items.map((item) => (
              <p key={item} style={{ ...bodyStyle, color: P.darkText, fontStyle: 'italic' }}>
                <Inline text={item} />
              </p>
            ))}
          </div>
        </div>
      );
    case 'note':
      return (
        <div
          style={{
            background: P.roseMist,
            border: `1px solid ${P.charm}`,
            borderRadius: 6,
            padding: '12px 14px',
          }}
        >
          <p style={{ ...bodyStyle, color: P.darkText }}>
            <strong style={{ color: P.maxRed }}>Obs.: </strong>
            <Inline text={block.text} />
          </p>
        </div>
      );
    case 'list':
      return (
        <ul style={{ ...bodyStyle, paddingLeft: 22, display: 'grid', gap: 4, margin: 0 }}>
          {block.items.map((item) => (
            <li key={item}>
              <Inline text={item} />
            </li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <div className="ga-table-wrap">
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14.5 }}>
            <thead>
              <tr>
                {block.header.map((cell) => (
                  <th
                    key={cell}
                    style={{
                      fontFamily: oswald,
                      fontWeight: 600,
                      fontSize: 12,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: P.darkText,
                      background: P.baby,
                      border: `1px solid ${P.lightBorder}`,
                      padding: '8px 10px',
                      textAlign: 'left',
                    }}
                  >
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  {row.map((cell, cellIdx) => (
                    <td
                      key={cellIdx}
                      style={{
                        border: `1px solid ${P.lightBorder}`,
                        padding: '8px 10px',
                        color: P.midText,
                        background: rowIdx % 2 ? P.blush : P.white,
                      }}
                    >
                      <Inline text={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

function GrammarTopicPage() {
  const { t } = useTranslation('p2e');
  const { topicSlug } = Route.useParams();
  const found = findGrammarTopic(topicSlug);
  // Founder-recorded explainer videos, keyed by topic slug (one small list
  // for the whole guide; absent rows simply mean "no video yet").
  const { data: videos } = trpc.grammarVideos.list.useQuery(undefined, { staleTime: 300_000 });

  if (!found) {
    return <Navigate to="/grammar-aid" replace />;
  }
  const { topic, group, prev, next } = found;

  const navLinkStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: oswald,
    fontWeight: 600,
    fontSize: 12,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: P.warmGray,
    textDecoration: 'none',
    maxWidth: '48%',
  };

  return (
    <div style={{ minHeight: '100vh', background: P.cream, borderRadius: 12 }}>
      <style dangerouslySetInnerHTML={{ __html: TOPIC_STYLES }} />

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 20px 56px' }}>
        <Link to="/grammar-aid" className="ga-nav-link" style={{ ...navLinkStyle, marginBottom: 18 }}>
          <ArrowLeft size={14} aria-hidden />
          {t('grammarAid.backToList')}
        </Link>

        <header style={{ margin: '14px 0 20px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
            <p
              style={{
                fontFamily: oswald,
                fontWeight: 600,
                fontSize: 11,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: P.maxRed,
                margin: 0,
              }}
            >
              {t(`grammarAid.groups.${group.id}`)}
            </p>
            <GrammarLevelPill level={topic.level} withName />
          </div>
          <h1
            style={{
              fontFamily: dmSerif,
              fontWeight: 400,
              fontSize: 32,
              lineHeight: 1.12,
              color: P.darkText,
              margin: '6px 0 0',
            }}
          >
            {topic.title}
          </h1>
        </header>

        {(() => {
          const videoUrl = videos?.find((video) => video.topicSlug === topic.slug)?.videoUrl;
          if (!videoUrl) return null;
          return (
            <section
              aria-label={t('grammarAid.videoTitle')}
              style={{
                marginBottom: 20,
                background: P.white,
                border: `1px solid ${P.lightBorder}`,
                borderRadius: 8,
                boxShadow: `6px 10px 22px ${P.shadow}`,
                padding: 14,
              }}
            >
              <p
                style={{
                  fontFamily: oswald,
                  fontWeight: 600,
                  fontSize: 11,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: P.maxRed,
                  margin: '0 0 10px',
                }}
              >
                {t('grammarAid.videoTitle')}
              </p>
              <video
                controls
                playsInline
                preload="metadata"
                src={videoUrl}
                style={{ width: '100%', borderRadius: 6, background: P.darkText }}
              />
            </section>
          );
        })()}

        <article
          style={{
            background: P.white,
            border: `1px solid ${P.lightBorder}`,
            borderRadius: 8,
            boxShadow: `6px 10px 22px ${P.shadow}`,
            padding: '24px 22px',
            display: 'grid',
            gap: 14,
          }}
        >
          {topic.blocks.map((block, index) => (
            <BlockView key={index} block={block} />
          ))}
        </article>

        {/* Interactive practice when the topic has exercises; placeholder otherwise. */}
        {GRAMMAR_EXERCISES[topic.slug] ? (
          <GrammarPractice key={topic.slug} exercises={GRAMMAR_EXERCISES[topic.slug]} />
        ) : (
        <section
          style={{
            marginTop: 20,
            background: P.blush,
            border: `1px dashed ${P.charm}`,
            borderRadius: 8,
            padding: '18px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              borderRadius: 6,
              background: P.maxRed,
              color: P.white,
              flexShrink: 0,
            }}
          >
            <PencilLine size={18} aria-hidden />
          </span>
          <div>
            <p
              style={{
                fontFamily: oswald,
                fontWeight: 600,
                fontSize: 13,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: P.darkText,
                margin: 0,
              }}
            >
              {t('grammarAid.practiceTitle')}
            </p>
            <p style={{ ...bodyStyle, marginTop: 4 }}>{t('grammarAid.practiceSoon')}</p>
          </div>
        </section>
        )}

        <nav
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            marginTop: 24,
          }}
        >
          {prev ? (
            <Link
              to="/grammar-aid/$topicSlug"
              params={{ topicSlug: prev.slug }}
              className="ga-nav-link"
              style={navLinkStyle}
            >
              <ArrowLeft size={14} style={{ flexShrink: 0 }} aria-hidden />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prev.title}</span>
            </Link>
          ) : (
            <span />
          )}
          {next ? (
            <Link
              to="/grammar-aid/$topicSlug"
              params={{ topicSlug: next.slug }}
              className="ga-nav-link"
              style={{ ...navLinkStyle, textAlign: 'right' }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{next.title}</span>
              <ArrowRight size={14} style={{ flexShrink: 0 }} aria-hidden />
            </Link>
          ) : (
            <span />
          )}
        </nav>
      </div>
    </div>
  );
}
